/**
 * Cairn Web Components Library
 *
 * This module exports all Cairn web components for use in both
 * the web documentation site and the mobile application.
 *
 * Components are built with Lit and follow web standards for
 * maximum compatibility and performance.
 */

// App components
export { CairnApp } from './app/cairn-app.js'

// Navigation components
export { CairnNavigation } from './navigation/cairn-navigation.js'

// Map components
export { CairnMap } from './map/cairn-map.js'

// UI components
export { CairnButton } from './ui/cairn-button.js'
export { CairnLoader } from './ui/cairn-loader.js'
export { CairnSplash } from './ui/cairn-splash.js'

// Location components
export { LocationSearch } from './location-search/location-search.js'

// Help system components
export { CairnHelpArticle } from './cairn-help/help-article.js'
export { CairnHelpSearch } from './cairn-help/help-search.js'

// Profile components
export { CairnProfile } from './profile/cairn-profile.js'
export { CairnSettings } from './profile/cairn-settings.js'

// Page components
export { CairnLogbook } from './trips/cairn-logbook.js'
export { PlanList } from './trips/plan-list.js'
export { PlanForm } from './trips/plan-form.js'
export { PlanDetail } from './trips/plan-detail.js'
export { ActivityList } from './trips/activity-list.js'
export { ActivityDetail } from './trips/activity-detail.js'
export { RoutesList } from './trips/routes-list.js'
export { RouteDetail } from './trips/route-detail.js'
export { TripMapPreview } from './trips/trip-map-preview.js'
export { CairnTeams } from './teams/cairn-teams.js'
export { CairnTeamDetail } from './teams/cairn-team-detail.js'
export { CairnTeamInviteClaim } from './teams/cairn-team-invite-claim.js'

/**
 * Define all custom elements for use in HTML
 * This ensures components are registered when the module is imported
 */
export function defineComponents(): void {
  // Components self-register via @customElement decorator
  // This function is provided for explicit registration if needed
}

/**
 * Component theme configuration
 */
export interface CairnThemeConfig {
  colors?: {
    primary?: string
    secondary?: string
    surface?: string
    background?: string
    text?: string
    textSecondary?: string
    border?: string
    error?: string
    warning?: string
    success?: string
    surfaceHover?: string
    primaryBgLight?: string
    errorDark?: string
    errorLight?: string
    // Material Design system color tokens
    mdSysPrimary?: string
    mdSysOnPrimary?: string
    mdSysSurface?: string
    mdSysOnSurface?: string
    mdSysOnSurfaceVariant?: string
    mdSysOutline?: string
    mdSysOutlineVariant?: string
    mdSysSurfaceContainerHighest?: string
    mdSysInverseSurface?: string
    mdSysInverseOnSurface?: string
  }
  spacing?: {
    xs?: string
    sm?: string
    md?: string
    lg?: string
    xl?: string
  }
  typography?: {
    fontFamily?: string
    fontSize?: {
      xs?: string
      sm?: string
      md?: string
      lg?: string
      xl?: string
    }
    fontWeight?: {
      normal?: string
      medium?: string
      bold?: string
    }
    lineHeight?: string
  }
  borderRadius?: string
  shadow?: string
  animation?: {
    duration?: string
    easing?: string
  }
}

/**
 * Apply theme configuration to CSS custom properties
 */
export function applyTheme(config: CairnThemeConfig): void {
  const root = document.documentElement

  if (config.colors) {
    // Set our cairn theme variables
    Object.entries(config.colors).forEach(([key, value]) => {
      if (key.startsWith('mdSys')) {
        // Convert mdSysPrimary -> --md-sys-color-primary
        const mdKey = key
          .replace('mdSys', '')
          .replace(/([A-Z])/g, '-$1')
          .toLowerCase()
          .slice(1)
        root.style.setProperty(`--md-sys-color-${mdKey}`, value)
      } else {
        // Regular cairn theme variables
        root.style.setProperty(
          `--cairn-${key.replace(/([A-Z])/g, '-$1').toLowerCase()}-color`,
          value
        )
      }
    })

    // Calculate textHover based on current theme
    const isLightTheme =
      config.colors.background &&
      (config.colors.background === '#f8fafc' || config.colors.background.includes('fafc'))
    // Light theme needs darker hover text, dark theme needs lighter hover text
    const textHoverColor = isLightTheme ? '#0f172a' : '#f1f5f9'
    root.style.setProperty('--cairn-text-hover-color', textHoverColor)

    // Set color scheme for browser/system components
    root.style.setProperty('color-scheme', isLightTheme ? 'light' : 'dark')

    // Set comprehensive Material Design tokens that components need
    const primaryColor = config.colors.primary ?? '#2d3748'
    const surfaceColor = config.colors.surface ?? '#ffffff'
    const surfaceHoverColor = config.colors.surfaceHover ?? '#f1f5f9'
    const textColor = config.colors.text ?? '#1e293b'
    const textSecondaryColor = config.colors.textSecondary ?? '#64748b'
    const borderColor = config.colors.border ?? '#e2e8f0'

    // Outlined Select Field tokens
    root.style.setProperty('--md-outlined-select-text-field-input-text-color', textColor)
    root.style.setProperty('--md-outlined-select-text-field-label-text-color', textSecondaryColor)
    root.style.setProperty(
      '--md-outlined-select-text-field-supporting-text-color',
      textSecondaryColor
    )
    root.style.setProperty('--md-outlined-select-text-field-container-color', surfaceColor)
    root.style.setProperty('--md-outlined-select-text-field-outline-color', borderColor)
    root.style.setProperty('--md-outlined-select-text-field-focus-outline-color', primaryColor)
    root.style.setProperty('--md-outlined-select-text-field-hover-outline-color', primaryColor)

    // Outlined Text Field tokens
    root.style.setProperty('--md-outlined-text-field-input-text-color', textColor)
    root.style.setProperty('--md-outlined-text-field-label-text-color', textSecondaryColor)
    root.style.setProperty('--md-outlined-text-field-supporting-text-color', textSecondaryColor)
    root.style.setProperty('--md-outlined-text-field-container-color', surfaceColor)
    root.style.setProperty('--md-outlined-text-field-outline-color', borderColor)
    root.style.setProperty('--md-outlined-text-field-focus-outline-color', primaryColor)
    root.style.setProperty('--md-outlined-text-field-hover-outline-color', primaryColor)

    // Menu container tokens
    root.style.setProperty('--md-menu-container-color', surfaceColor)
    root.style.setProperty('--md-menu-container-surface-tint-layer-color', surfaceColor)

    // List item tokens (for select options)
    root.style.setProperty('--md-list-item-label-text-color', textColor)
    root.style.setProperty('--md-list-item-supporting-text-color', textSecondaryColor)
    root.style.setProperty('--md-list-item-container-color', surfaceColor)
    root.style.setProperty('--md-list-item-hover-state-layer-color', surfaceHoverColor)
    root.style.setProperty('--md-list-item-focus-state-layer-color', surfaceHoverColor)
    root.style.setProperty('--md-list-item-pressed-state-layer-color', surfaceHoverColor)
    root.style.setProperty('--md-list-item-selected-container-color', surfaceHoverColor)
    root.style.setProperty('--md-list-item-selected-label-text-color', textColor)
    root.style.setProperty('--md-list-item-selected-trailing-icon-color', primaryColor)
    root.style.setProperty('--md-list-item-selected-leading-icon-color', primaryColor)

    // Menu item specific tokens (might be different from list-item)
    root.style.setProperty('--md-menu-item-selected-container-color', surfaceHoverColor)
    root.style.setProperty('--md-menu-item-selected-label-text-color', textColor)
    root.style.setProperty('--md-menu-item-hover-label-text-color', textColor)
    root.style.setProperty('--md-menu-item-focus-label-text-color', textColor)

    // Switch tokens - comprehensive coverage
    root.style.setProperty('--md-switch-selected-track-color', primaryColor)
    root.style.setProperty('--md-switch-selected-handle-color', surfaceColor)
    root.style.setProperty('--md-switch-selected-icon-color', primaryColor)
    root.style.setProperty('--md-switch-selected-pressed-handle-color', surfaceColor)
    root.style.setProperty('--md-switch-selected-pressed-track-color', primaryColor)
    root.style.setProperty('--md-switch-selected-focus-handle-color', surfaceColor)
    root.style.setProperty('--md-switch-selected-focus-track-color', primaryColor)
    root.style.setProperty('--md-switch-selected-hover-handle-color', surfaceColor)
    root.style.setProperty('--md-switch-selected-hover-track-color', primaryColor)
    root.style.setProperty('--md-switch-unselected-track-color', borderColor)
    root.style.setProperty('--md-switch-unselected-handle-color', surfaceColor)
    root.style.setProperty('--md-switch-unselected-icon-color', borderColor)
    root.style.setProperty('--md-switch-unselected-pressed-handle-color', surfaceColor)
    root.style.setProperty('--md-switch-unselected-pressed-track-color', borderColor)
    root.style.setProperty('--md-switch-unselected-focus-handle-color', surfaceColor)
    root.style.setProperty('--md-switch-unselected-focus-track-color', borderColor)
    root.style.setProperty('--md-switch-unselected-hover-handle-color', surfaceColor)
    root.style.setProperty('--md-switch-unselected-hover-track-color', borderColor)

    // Switch sizing tokens to make handle smaller
    root.style.setProperty('--md-switch-touch-target-size', '20px')
    root.style.setProperty('--md-switch-selected-handle-height', '20px')
    root.style.setProperty('--md-switch-selected-handle-width', '20px')
    root.style.setProperty('--md-switch-handle-shape', '10px') // Border radius for smaller handle
    root.style.setProperty('--md-switch-track-shape', '12px') // Track border radius

    // Radio tokens
    root.style.setProperty('--md-radio-selected-icon-color', primaryColor)
    root.style.setProperty('--md-radio-unselected-icon-color', borderColor)
    root.style.setProperty('--md-radio-hover-state-layer-color', surfaceHoverColor)
    root.style.setProperty('--md-radio-focus-state-layer-color', surfaceHoverColor)

    // Override any remaining default Material purple colors with our blue
    root.style.setProperty('--md-ref-palette-primary40', primaryColor) // Default Material purple
    root.style.setProperty('--md-comp-outlined-text-field-focus-outline-color', primaryColor)
    root.style.setProperty('--md-comp-outlined-text-field-hover-outline-color', primaryColor)
  }

  if (config.spacing) {
    Object.entries(config.spacing).forEach(([key, value]) => {
      root.style.setProperty(`--cairn-spacing-${key}`, value)
    })
  }

  if (config.typography) {
    if (config.typography.fontFamily) {
      root.style.setProperty('--cairn-font-family', config.typography.fontFamily)
    }

    if (config.typography.fontSize) {
      Object.entries(config.typography.fontSize).forEach(([key, value]) => {
        root.style.setProperty(`--cairn-text-size-${key}`, value)
      })
    }

    if (config.typography.fontWeight) {
      Object.entries(config.typography.fontWeight).forEach(([key, value]) => {
        root.style.setProperty(`--cairn-font-weight-${key}`, value)
      })
    }

    if (config.typography.lineHeight) {
      root.style.setProperty('--cairn-line-height', config.typography.lineHeight)
    }
  }

  if (config.borderRadius) {
    root.style.setProperty('--cairn-border-radius', config.borderRadius)
  }

  if (config.shadow) {
    root.style.setProperty('--cairn-shadow', config.shadow)
  }

  if (config.animation) {
    if (config.animation.duration) {
      root.style.setProperty('--cairn-animation-duration', config.animation.duration)
    }
    if (config.animation.easing) {
      root.style.setProperty('--cairn-animation-easing', config.animation.easing)
    }
  }
}

/**
 * Light theme configuration using cairn icon colors
 */
export const lightTheme: CairnThemeConfig = {
  colors: {
    primary: '#2d3748', // Bottom stone - rich dark blue-gray
    secondary: '#64748b',
    surface: '#ffffff',
    surfaceHover: '#eff6ff', // Very light blue tint for better hover feedback
    primaryBgLight: 'rgba(45, 55, 72, 0.04)',
    background: '#f8fafc',
    text: '#1e293b',
    textSecondary: '#64748b',
    // textHover will be calculated in applyTheme for better contrast
    border: '#e2e8f0',
    error: '#dc2626',
    errorDark: '#b91c1c',
    errorLight: '#ef4444',
    warning: '#f59e0b',
    success: '#059669',
    // Material Design system tokens for light theme
    mdSysPrimary: '#2d3748',
    mdSysOnPrimary: '#ffffff',
    mdSysSurface: '#ffffff',
    mdSysOnSurface: '#1e293b',
    mdSysOnSurfaceVariant: '#64748b',
    mdSysOutline: '#e2e8f0',
    mdSysOutlineVariant: '#64748b',
    mdSysSurfaceContainerHighest: '#ffffff',
    mdSysInverseSurface: '#1e293b',
    mdSysInverseOnSurface: '#ffffff',
  },
  spacing: {
    xs: '4px',
    sm: '8px',
    md: '16px',
    lg: '24px',
    xl: '32px',
  },
  typography: {
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    fontSize: {
      xs: '12px',
      sm: '14px',
      md: '16px',
      lg: '20px',
      xl: '24px',
    },
    fontWeight: {
      normal: '400',
      medium: '500',
      bold: '600',
    },
    lineHeight: '1.5',
  },
  borderRadius: '8px',
  shadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
  animation: {
    duration: '0.2s',
    easing: 'ease-in-out',
  },
}

/**
 * Dark theme configuration using lighter cairn icon colors
 */
export const darkTheme: CairnThemeConfig = {
  colors: {
    primary: '#718096', // Top stone - lighter blue-gray for contrast
    secondary: '#a0aec0',
    surface: '#1a202c',
    surfaceHover: '#334155', // Slightly lighter blue-gray
    primaryBgLight: 'rgba(113, 128, 150, 0.08)',
    background: '#0f131a',
    text: '#ffffff',
    textSecondary: '#cbd5e1',
    // textHover will be calculated in applyTheme for better contrast
    border: '#64748b', // Brighter border to match outline
    error: '#fc8181',
    errorDark: '#e53e3e',
    errorLight: '#feb2b2',
    warning: '#f6ad55',
    success: '#68d391',
    // Material Design system tokens for dark theme
    mdSysPrimary: '#718096',
    mdSysOnPrimary: '#ffffff',
    mdSysSurface: '#1a202c',
    mdSysOnSurface: '#ffffff',
    mdSysOnSurfaceVariant: '#cbd5e1',
    mdSysOutline: '#64748b',
    mdSysOutlineVariant: '#cbd5e1',
    mdSysSurfaceContainerHighest: '#1a202c',
    mdSysInverseSurface: '#ffffff',
    mdSysInverseOnSurface: '#1e293b',
  },
  spacing: {
    xs: '4px',
    sm: '8px',
    md: '16px',
    lg: '24px',
    xl: '32px',
  },
  typography: {
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    fontSize: {
      xs: '12px',
      sm: '14px',
      md: '16px',
      lg: '20px',
      xl: '24px',
    },
    fontWeight: {
      normal: '400',
      medium: '500',
      bold: '600',
    },
    lineHeight: '1.5',
  },
  borderRadius: '8px',
  shadow: '0 4px 8px rgba(0, 0, 0, 0.3)',
  animation: {
    duration: '0.2s',
    easing: 'ease-in-out',
  },
}

export const defaultTheme = darkTheme

function getInitialTheme(): CairnThemeConfig {
  try {
    const stored = localStorage.getItem('cairn_user_settings')
    if (stored) {
      const { theme } = JSON.parse(stored)
      if (theme === 'light') return lightTheme
      if (theme === 'dark') return darkTheme
    }
  } catch {}
  if (window.matchMedia('(prefers-color-scheme: light)').matches) return lightTheme
  return darkTheme
}

applyTheme(getInitialTheme())
